// ManagerSettingDialog.jsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  useTheme,
  Box,
  InputAdornment,
  IconButton,
  Typography,
  MenuItem,
} from "@mui/material";
import axios from "axios";
import {
  AccountCircle,
  EmailRounded,
  Lock,
  Phone,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { tokens } from "../theme";
import { useUser } from "../provider/AuthProvider";

const roles = ["TENANT", "LANDLORD", "SERVICE_PROVIDER"];

const UserAddDialog = ({ open, onClose, onAdd, user_type }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user } = useUser();

  const [availableServiceTypes, setAvailableServiceTypes] = useState([]);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    role: user_type || "TENANT",
    serviceType: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Reset form and fetch service types on dialog open
  useEffect(() => {
    if (open) {
      // Reset form with user_type preselected
      setFormData({
        username: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        role: user_type || "TENANT",
        serviceType: "",
      });

      // Fetch service types only for SERVICE_PROVIDER
      if (user_type === "SERVICE_PROVIDER") {
        const fetchServiceTypes = async () => {
          try {
            const res = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/cmt/availableServiceTypeList/${user.id}`
            );
            setAvailableServiceTypes(res.data || []);
          } catch (error) {
            console.error("Failed to fetch service types:", error);
          }
        };
        fetchServiceTypes();
      }
    }
  }, [open, user_type, user.id]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = async () => {
    setIsSubmit(true);
    await onAdd(formData); // Call parent submit handler
    setIsSubmit(false);
    onClose(); // Close dialog after add
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 1.5,
            px: 3,
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography>Create User Account</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={2}>
            {user_type ? (
              <TextField label="Role" value={user_type} fullWidth disabled />
            ) : (
              <TextField
                select
                label="Role"
                value={formData.role}
                onChange={handleChange("role")}
                fullWidth
              >
                {roles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Username"
              name="username"
              fullWidth
              value={formData.username}
              onChange={handleChange("username")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Phone"
              name="phone"
              fullWidth
              value={formData.phone}
              onChange={handleChange("phone")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Email"
              name="email"
              fullWidth
              value={formData.email}
              onChange={handleChange("email")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailRounded />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              fullWidth
              value={formData.password}
              onChange={handleChange("password")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#90a4ae" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showPassword ? "text" : "password"}
              name="confirmPassword"
              fullWidth
              value={formData.confirmPassword}
              onChange={handleChange("confirmPassword")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />

            {/* Conditional Service Type Dropdown */}
            {formData.role === "SERVICE_PROVIDER" && (
              <TextField
                select
                label="Service Type"
                fullWidth
                value={formData.serviceType}
                onChange={handleChange("serviceType")}
                margin="normal"
              >
                {availableServiceTypes.map((type) => (
                  <MenuItem key={type.name} value={type.name}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "space-between", px: 3, pb: 2, pt: 1 }}
        >
          <Box display="flex" gap={1}>
            <Button
              onClick={handleSubmit}
              disabled={isSubmit}
              variant="contained"
            >
              {isSubmit ? "Saving..." : "Add User"}
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserAddDialog;
